/**
 * 005-packing-verification, admin-packing-oversight bucket. T058 — Super Admin create/edit of
 * tolerance rules via `POST`/`PUT /oversight/tolerance-rules` (FR-035, D-26, I-03).
 *
 * No business-rule validation happens client-side: `tolerance_negative` and
 * `tolerance_hard_limit_too_low` are the API's own refusals (`ToleranceRule.Validate`, verified in
 * source) and constitution I says this app submits what was entered and renders the server's
 * verbatim rejection — it does not pre-judge whether a hard limit or a percentage is sensible. The
 * zod schema below checks only that a number was typed, nothing about its value.
 *
 * A `PUT` supersedes rather than edits (`ToleranceRule.Supersede`): the response is a *new* row at
 * `version + 1`, and the row being edited stays in history, superseded. Editing here means "load
 * the standing row's values into the form, submit against its id" — the list simply reloads
 * afterwards and shows the new standing row.
 */
import { useState } from "react";
import { useLoaderData, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { PiPencilSimpleDuotone } from "react-icons/pi";
import { Alert } from "rizzui";
import { RowActions } from "../../components/table/row-actions";
import { Badge as DomainBadge, WidgetCard, ErrorSurface, EmptyState, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import type { ColumnDef } from "@tanstack/react-table";
import FormFooter from "@core/components/form-footer";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { activeStatusOptions, TableSearch } from "../../components/table/toolbar";
import { can } from "../../lib/permissions";
import { hrefWith } from "../../lib/paging";
import {
  getToleranceRules,
  createToleranceRule,
  updateToleranceRule,
  toleranceScopeLabel,
  type ToleranceRuleRow,
  type ToleranceScope,
} from "../../lib/services/warehouse";
import { BrandPicker, CategoryPicker, SkuPicker } from "../../components/catalog-pickers";
import type { AccountSummary, PermissionState } from "../../types";

const SCOPES: ToleranceScope[] = ["sku", "brand", "category", "system"];

/**
 * All / Active / Inactive, as every active-flagged list offers: Active (the default) is the standing
 * rules, All every version (`?history=1`), Inactive the superseded ones (`?superseded=1`). The route
 * returns the whole history in one response, so it is read once and every view — and every count
 * the status dropdown shows — is a count of rows the API returned.
 */
export async function toleranceRulesLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const view: "all" | "active" | "inactive" = params.get("superseded") === "1" ? "inactive" : params.get("history") === "1" ? "all" : "active";
  const history = await getToleranceRules(true);
  const standing = history.filter((rule) => rule.isActive);
  const superseded = history.filter((rule) => !rule.isActive);

  return {
    rules: view === "all" ? history : view === "active" ? standing : superseded,
    view,
    counts: { all: history.length, active: standing.length, inactive: superseded.length },
  };
}

function pct(value: number | string): string {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? `${n}%` : "—";
}

const NUMBER_FIELDS = ["minVariationPct", "maxVariationPct", "hardLimitPct", "absoluteFloorKg"] as const;

// One superRefine for every check, not per-field regexes plus object refines: zod skips an object's
// refinements while any field fails, so "choose a target" only appeared once all four numbers were
// fixed. This way every error on the form shows at once.
const ruleSchema = z
  .object({
    scope: z.enum(["sku", "brand", "category", "system"]),
    scopeId: z.string().trim().optional(),
    minVariationPct: z.string().trim(),
    maxVariationPct: z.string().trim(),
    hardLimitPct: z.string().trim(),
    absoluteFloorKg: z.string().trim(),
  })
  .superRefine((v, ctx) => {
    for (const field of NUMBER_FIELDS) {
      if (!/^\d+(\.\d+)?$/.test(v[field])) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a number", path: [field] });
    }
    if (v.scope !== "system" && !v.scopeId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Required for every scope except System", path: ["scopeId"] });
    if (v.scope === "system" && v.scopeId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "A system rule carries no scope id", path: ["scopeId"] });
  });
type RuleFormValues = z.infer<typeof ruleSchema>;

const blankValues: RuleFormValues = { scope: "system", scopeId: "", minVariationPct: "", maxVariationPct: "", hardLimitPct: "", absoluteFloorKg: "" };

/** `onEdit` is a state setter — stable, so `PagedTable` freezing its columns at mount is harmless. */
function ruleColumns(onEdit: (rule: ToleranceRuleRow) => void): ColumnDef<ToleranceRuleRow>[] {
  return [
    // Accessors on scope/scope id are what the search box matches — TanStack only global-filters a column with one.
    { id: "scope", accessorKey: "scope", header: "Scope", cell: ({ row }) => toleranceScopeLabel(row.original.scope) },
    {
      id: "scopeId",
      // The API's own `scopeName` (PAK-04); a scope it could not resolve reads "—", never the id.
      accessorFn: (row) => row.scopeName ?? "",
      header: "Applies to",
      cell: ({ row }) => row.original.scopeName ?? "—",
    },
    // The four numbers and the version sort on the API's own value read as a number; shown as sent.
    { id: "minVariationPct", accessorFn: (row) => Number(row.minVariationPct), header: "Allowed shortfall", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{pct(row.original.minVariationPct)}</span> },
    { id: "maxVariationPct", accessorFn: (row) => Number(row.maxVariationPct), header: "Allowed excess", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{pct(row.original.maxVariationPct)}</span> },
    { id: "hardLimitPct", accessorFn: (row) => Number(row.hardLimitPct), header: "Hard limit", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{pct(row.original.hardLimitPct)}</span> },
    { id: "absoluteFloorKg", accessorFn: (row) => Number(row.absoluteFloorKg), header: "Floor", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{row.original.absoluteFloorKg} kg</span> },
    { id: "version", accessorFn: (row) => Number(row.version), header: "Version", meta: { align: "right" }, cell: ({ row }) => <span className="font-semibold tabular-nums">v{row.original.version}</span> },
    {
      id: "isActive",
      accessorKey: "isActive",
      header: "Standing",
      cell: ({ row }) => <DomainBadge kind={row.original.isActive ? "active" : "inactive"}>{row.original.isActive ? "Standing" : "Superseded"}</DomainBadge>,
    },
    {
      id: "actions",
      header: "",
      // The row's one ⋮ menu; only a standing rule can be superseded, so a superseded row has none.
      cell: ({ row }) =>
        row.original.isActive ? (
          <RowActions
            name={`the ${toleranceScopeLabel(row.original.scope)} rule`}
            primary={{ label: "Edit rule", icon: PiPencilSimpleDuotone, onClick: () => onEdit(row.original) }}
          />
        ) : null,
    },
  ];
}

export default function ToleranceRulesPage() {
  const { rules, view, counts } = useLoaderData() as Awaited<ReturnType<typeof toleranceRulesLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const [editing, setEditing] = useState<ToleranceRuleRow | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [resetValues, setResetValues] = useState<RuleFormValues | null>(null);

  if (!can(permissionState, "packing.rules.administer")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "Tolerance rules are Super Admin only." }}
        action="administer tolerance rules"
      />
    );
  }

  const defaultValues = editing
    ? {
        scope: editing.scope,
        scopeId: editing.scopeId ?? "",
        minVariationPct: String(editing.minVariationPct),
        maxVariationPct: String(editing.maxVariationPct),
        hardLimitPct: String(editing.hardLimitPct),
        absoluteFloorKg: String(editing.absoluteFloorKg),
      }
    : blankValues;

  async function submit(values: RuleFormValues) {
    setError(null);
    const numbers = {
      minVariationPct: Number(values.minVariationPct),
      maxVariationPct: Number(values.maxVariationPct),
      hardLimitPct: Number(values.hardLimitPct),
      absoluteFloorKg: Number(values.absoluteFloorKg),
    };
    try {
      if (editing) {
        const updated = await updateToleranceRule(editing.id, numbers);
        toast.success(`Rule superseded — now version ${updated.version}`);
        setEditing(null);
      } else {
        await createToleranceRule({ scope: values.scope, scopeId: values.scope === "system" ? null : (values.scopeId ?? null), ...numbers });
        toast.success("Rule created");
      }
      setResetValues(blankValues);
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error(editing ? "Could not update this rule" : "Could not create this rule");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Tolerance Rules"
        count={rules.length}
        description="A change reaches devices on their next cache refresh — it takes effect on the next line packed, with no software release. Editing writes a new version; the row it replaces stays in history, unedited, so a variance dispute can still see the rule that judged it."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Packing" }, { name: "Tolerance Rules" }]}
      />

      {/* No paging on this route, so PagedTable pages the whole rule set in memory. */}
      <PagedTable<ToleranceRuleRow>
        result={{ items: rules, page: 1, pageSize: rules.length, totalCount: rules.length }}
        caption="Tolerance rules, with scope, the four numbers, version and standing"
        columns={ruleColumns((rule) => {
          setEditing(rule);
          // A pending reset from an earlier create/cancel (still truthy in state) would otherwise
          // re-fire on this remount and blank the row's real values right after they're loaded.
          setResetValues(null);
          // The form it loads into sits below the grid — bring it into view.
          requestAnimationFrame(() => document.getElementById("rule-form")?.scrollIntoView?.({ behavior: "smooth", block: "start" }));
        })}
        getRowId={(row) => row.id}
        // All / Active (standing) / Inactive (superseded) — see the loader.
        status={{
          label: "Status",
          options: activeStatusOptions(
            view,
            (next) =>
              hrefWith("/packing/tolerance-rules", searchParams, {
                history: next === "all" ? "1" : undefined,
                superseded: next === "inactive" ? "1" : undefined,
              }),
            counts,
          ),
        }}
        search={(table) => <TableSearch table={table} placeholder="Search rules…" />}
        clear={{ active: view !== "active", href: hrefWith("/packing/tolerance-rules", searchParams, { history: undefined, superseded: undefined }) }}
        empty={<EmptyState title="No tolerance rules yet">No rule exists at any scope — every line will report a data defect rather than a band until one is added.</EmptyState>}
      />

      <div id="rule-form" />
      <WidgetCard title={<>{editing ? `Edit rule — ${toleranceScopeLabel(editing.scope)}` : "Add a rule"}</>} headerClassName="border-muted border-b">
        {error ? <div className="p-4 pb-0"><ErrorSurface error={error} action={editing ? "update this rule" : "create this rule"} /></div> : null}

        <Form<RuleFormValues>
          key={editing?.id ?? "new"}
          validationSchema={ruleSchema}
          useFormProps={{ defaultValues }}
          resetValues={resetValues}
          className="flex flex-col gap-6 p-4 @container"
          onSubmit={submit}
        >
          {({ register, watch, setValue, formState: { errors, isSubmitting } }) => {
            const currentScope = watch("scope");
            const scopeIdError = errors.scopeId?.message;
            const scopeIdValue = watch("scopeId") ?? "";
            const onScopeIdChange = (id: string) => setValue("scopeId", id, { shouldValidate: true });

            return (
            <>
              <FormGroup title="Scope" description={editing ? "The scope of a standing rule cannot change — supersede its numbers instead." : "What this rule applies to. Most specific wins: SKU beats Brand beats Category beats System."}>
                <SelectField label="Scope" required error={errors.scope?.message} disabled={!!editing} {...register("scope")}>
                  {SCOPES.map((s) => (
                    <option key={s} value={s}>
                      {toleranceScopeLabel(s)}
                    </option>
                  ))}
                </SelectField>
                {/* PAK-12: a searchable picker per scope, replacing a raw-GUID text box — System carries no scope id at all. */}
                {currentScope === "sku" ? (
                  <SkuPicker value={scopeIdValue} onChange={onScopeIdChange} error={scopeIdError} disabled={!!editing} required />
                ) : currentScope === "brand" ? (
                  <BrandPicker value={scopeIdValue} onChange={onScopeIdChange} error={scopeIdError} disabled={!!editing} required />
                ) : currentScope === "category" ? (
                  <CategoryPicker value={scopeIdValue} onChange={onScopeIdChange} error={scopeIdError} disabled={!!editing} required />
                ) : null}
              </FormGroup>

              <FormGroup title="The four numbers" description="Percentages as whole numbers (2 means 2%), the floor in kilograms.">
                <Field label="Allowed shortfall %" required inputMode="decimal" hint="How far under the expected weight still passes." error={errors.minVariationPct?.message} {...register("minVariationPct")} />
                <Field label="Allowed excess %" required inputMode="decimal" hint="How far over the expected weight still passes." error={errors.maxVariationPct?.message} {...register("maxVariationPct")} />
                <Field label="Hard limit %" required inputMode="decimal" hint="Must be at least the allowed shortfall and the allowed excess, or the amber band does not exist." error={errors.hardLimitPct?.message} {...register("hardLimitPct")} />
                <Field label="Absolute floor (kg)" required inputMode="decimal" error={errors.absoluteFloorKg?.message} {...register("absoluteFloorKg")} />
              </FormGroup>

              <FormFooter
                submitBtnText={isSubmitting ? "Saving…" : editing ? "Save as new version" : "Create rule"}
                isLoading={isSubmitting}
                altBtnText={editing ? "Cancel edit" : "Clear"}
                // A fresh object each time: `Form` only resets when `resetValues` changes identity,
                // so re-passing `blankValues` after a create would leave a typed-in form untouched.
                handleAltBtn={() => {
                  setEditing(null);
                  setResetValues({ ...blankValues });
                }}
              />
            </>
            );
          }}
        </Form>
      </WidgetCard>

      {rules.some((r) => !r.isActive) ? (
        <Alert color="info" variant="flat">
          Superseded rules stay in history so entries recorded against an old version remain traceable — they are not editable.
        </Alert>
      ) : null}
    </div>
  );
}
